import React from 'react';
import {
  X,
  Shield,
  ShieldCheck,
  Fingerprint,
  Landmark,
  Smartphone,
  Trash2,
  Plus,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  KeyRound,
  Eye,
  EyeOff,
  LogOut,
} from 'lucide-react';
import { PORTAL_NAV_LITIGATION_LABEL, PORTAL_NAV_VERTRAG_QA_LABEL } from '../portalNavigation';

/** Schwarzer Grund, feine lila Kante — wie der Kontoblock. Knöpfe kompakt, nicht gedehnt. */
const SETTINGS_SECTION = 'rounded-lg border border-violet-400/30 bg-[#07070a] p-3 space-y-3';
const SETTINGS_BTN =
  'inline-flex w-full items-center gap-2 rounded-lg border border-violet-400/30 bg-[#07070a] px-3 py-1.5 text-left text-sm text-slate-100 hover:border-violet-400/50 disabled:cursor-not-allowed disabled:opacity-50';
const SETTINGS_FIELD =
  'w-full bg-[#07070a] border border-violet-400/30 rounded-md px-3 py-1.5 text-sm text-slate-100 focus:outline-none focus:border-violet-400 placeholder-slate-600';

export interface PasskeyInfo {
  id: string;
  deviceName: string;
  created: string;
}

export interface SettingsPanelProps {
  onClose: () => void;
  onLogout: () => void;
  isPremium: boolean;
  supportsPasskeys: boolean;
  passkeys: PasskeyInfo[];
  pkLoading: boolean;
  pkRegistering: boolean;
  pkDeviceName: string;
  onPkDeviceNameChange: (v: string) => void;
  pkError: string;
  pkSuccess: string;
  onAddPasskey: () => void;
  onDeletePasskey: (id: string) => void;
  idAustriaConfigured: boolean;
  idAustriaLinked: boolean;
  idAustriaLoading: boolean;
  idAustriaBusy: boolean;
  idAustriaError: string;
  idAustriaMessage: string;
  onLinkIdAustria: () => void;
  onUnlinkIdAustria: () => void;
  pwOld: string;
  pwNew: string;
  pwConfirm: string;
  onPwOldChange: (v: string) => void;
  onPwNewChange: (v: string) => void;
  onPwConfirmChange: (v: string) => void;
  showPwOld: boolean;
  showPwNew: boolean;
  showPwConfirm: boolean;
  onToggleShowPwOld: () => void;
  onToggleShowPwNew: () => void;
  onToggleShowPwConfirm: () => void;
  pwError: string;
  pwSuccess: boolean;
  pwLoading: boolean;
  onPasswordChange: (e: React.FormEvent) => void;
}

export const SettingsPanel: React.FC<SettingsPanelProps> = ({
  onClose,
  onLogout,
  isPremium,
  supportsPasskeys,
  passkeys,
  pkLoading,
  pkRegistering,
  pkDeviceName,
  onPkDeviceNameChange,
  pkError,
  pkSuccess,
  onAddPasskey,
  onDeletePasskey,
  idAustriaConfigured,
  idAustriaLinked,
  idAustriaLoading,
  idAustriaBusy,
  idAustriaError,
  idAustriaMessage,
  onLinkIdAustria,
  onUnlinkIdAustria,
  pwOld,
  pwNew,
  pwConfirm,
  onPwOldChange,
  onPwNewChange,
  onPwConfirmChange,
  showPwOld,
  showPwNew,
  showPwConfirm,
  onToggleShowPwOld,
  onToggleShowPwNew,
  onToggleShowPwConfirm,
  pwError,
  pwSuccess,
  pwLoading,
  onPasswordChange,
}) => (
  <>
    <div className="flex items-center justify-between px-4 py-3 border-b border-violet-400/30 flex-shrink-0">
      <h2 className="text-slate-100 font-semibold">Einstellungen</h2>
      <button
        type="button"
        onClick={onClose}
        className="inline-flex items-center justify-center rounded-lg border border-violet-400/30 bg-[#07070a] p-1.5 text-violet-200 hover:border-violet-400/50"
        aria-label="Schließen"
      >
        <X className="w-4 h-4" />
      </button>
    </div>

    <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="portal-settings-pane">
      {!isPremium && (
        <section className={SETTINGS_SECTION}>
          <p className="text-sm text-slate-100">Auf Anfrage</p>
          <p className="text-sm text-slate-400 leading-relaxed">
            {PORTAL_NAV_LITIGATION_LABEL}, {PORTAL_NAV_VERTRAG_QA_LABEL} und der Dokument-Editor.
          </p>
          <a href="mailto:office@arkorbit.at?subject=Anfrage" className={SETTINGS_BTN}>
            Anfragen
          </a>
        </section>
      )}

      {supportsPasskeys && (
        <section className={SETTINGS_SECTION}>
          <div className="flex items-center gap-2">
            <Fingerprint className="w-4 h-4 text-violet-200" />
            <h3 className="text-slate-100 text-sm font-medium">Passkeys</h3>
          </div>

          {pkLoading ? (
            <p className="text-slate-400 text-sm flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Laden…
            </p>
          ) : passkeys.length > 0 ? (
            <ul className="space-y-1">
              {passkeys.map((pk) => (
                <li
                  key={pk.id}
                  className="flex items-center justify-between gap-2 border-b border-violet-400/15 px-1 py-1.5"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Smartphone className="w-4 h-4 text-violet-200/70 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-slate-100 truncate">{pk.deviceName}</p>
                      <p className="text-[11px] text-slate-400">
                        {new Date(pk.created).toLocaleDateString('de-AT')}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onDeletePasskey(pk.id)}
                    className="text-slate-400 hover:text-red-300 p-1"
                    title="Löschen"
                    aria-label="Passkey löschen"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-slate-400 text-sm">Noch kein Passkey.</p>
          )}

          <input
            type="text"
            value={pkDeviceName}
            onChange={(e) => onPkDeviceNameChange(e.target.value)}
            placeholder="Name des Geräts, optional"
            className={SETTINGS_FIELD}
          />
          <button type="button" onClick={onAddPasskey} disabled={pkRegistering} className={SETTINGS_BTN}>
            {pkRegistering ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Wird eingerichtet…
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" /> Passkey hinzufügen
              </>
            )}
          </button>

          {pkError && (
            <p className="text-red-300 text-xs flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5" /> {pkError}
            </p>
          )}
          {pkSuccess && (
            <p className="text-emerald-300 text-xs flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5" /> {pkSuccess}
            </p>
          )}

          <p className="text-slate-400 text-[11px] flex items-start gap-2">
            <ShieldCheck className="w-3.5 h-3.5 text-violet-200 flex-shrink-0 mt-0.5" />
            Die Erkennung am Gerät bleibt auf dem Gerät.
          </p>
        </section>
      )}

      <section className={SETTINGS_SECTION}>
        <div className="flex items-center gap-2">
          <Landmark className="w-4 h-4 text-violet-200" />
          <h3 className="text-slate-100 text-sm font-medium">ID Austria</h3>
        </div>
        {idAustriaLoading ? (
          <p className="text-slate-400 text-sm flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Laden…
          </p>
        ) : !idAustriaConfigured ? (
          <p className="text-slate-400 text-sm">ID Austria ist hier noch nicht eingerichtet.</p>
        ) : idAustriaLinked ? (
          <p className="text-slate-100 text-sm">Mit ID Austria verbunden.</p>
        ) : (
          <p className="text-slate-400 text-sm">Noch nicht verbunden.</p>
        )}
        {idAustriaConfigured ? (
          idAustriaLinked ? (
            <button type="button" onClick={onUnlinkIdAustria} disabled={idAustriaBusy} className={SETTINGS_BTN}>
              {idAustriaBusy ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Wird getrennt…
                </>
              ) : (
                'Verbindung trennen'
              )}
            </button>
          ) : (
            <button type="button" onClick={onLinkIdAustria} disabled={idAustriaBusy} className={SETTINGS_BTN}>
              {idAustriaBusy ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Weiter zur Anmeldung…
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" /> ID Austria verbinden
                </>
              )}
            </button>
          )
        ) : (
          <button type="button" disabled className={SETTINGS_BTN}>
            ID Austria ist hier noch nicht eingerichtet
          </button>
        )}
        {idAustriaError && (
          <p className="text-red-300 text-xs flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5" /> {idAustriaError}
          </p>
        )}
        {idAustriaMessage && (
          <p className="text-emerald-300 text-xs flex items-center gap-2">
            <CheckCircle2 className="w-3.5 h-3.5" /> {idAustriaMessage}
          </p>
        )}
        <p className="text-slate-400 text-[11px] leading-relaxed">
          Die Anmeldung läuft über ID Austria. Das Passwort bleibt zusätzlich. Das ist keine qualifizierte Signatur.
        </p>
      </section>

      <section className={SETTINGS_SECTION}>
        <h3 className="text-slate-100 text-sm font-medium flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-violet-200" /> Passwort
        </h3>
        <p className="text-slate-400 text-xs leading-relaxed">
          Das Passwort bleibt, auch wenn ein Passkey oder ID Austria eingerichtet ist. Ändern geht hier mit dem
          aktuellen Passwort.
        </p>
        <form onSubmit={onPasswordChange} className="space-y-3">
          {[
            {
              label: 'Aktuelles Passwort',
              val: pwOld,
              set: onPwOldChange,
              show: showPwOld,
              toggle: onToggleShowPwOld,
              auto: 'current-password',
            },
            {
              label: 'Neues Passwort',
              val: pwNew,
              set: onPwNewChange,
              show: showPwNew,
              toggle: onToggleShowPwNew,
              auto: 'new-password',
            },
            {
              label: 'Passwort bestätigen',
              val: pwConfirm,
              set: onPwConfirmChange,
              show: showPwConfirm,
              toggle: onToggleShowPwConfirm,
              auto: 'new-password',
            },
          ].map(({ label, val, set, show, toggle, auto }) => (
            <div key={label}>
              <label className="text-[11px] text-slate-400 mb-1 block">{label}</label>
              <div className="relative">
                <input
                  type={show ? 'text' : 'password'}
                  value={val}
                  onChange={(e) => set(e.target.value)}
                  autoComplete={auto}
                  className={SETTINGS_FIELD + ' pr-10'}
                />
                <button
                  type="button"
                  onClick={toggle}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-100 p-1"
                  aria-label={show ? 'Passwort verbergen' : 'Passwort zeigen'}
                >
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          ))}

          {pwError && (
            <p className="text-red-300 text-xs flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5" /> {pwError}
            </p>
          )}
          {pwSuccess && (
            <p className="text-emerald-300 text-xs flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5" /> Passwort gespeichert.
            </p>
          )}

          <button type="submit" disabled={pwLoading} className={SETTINGS_BTN}>
            {pwLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Passwort speichern'}
          </button>
        </form>
      </section>

      <div className="flex items-start gap-2 text-[11px] text-slate-400">
        <Shield className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-violet-200" />
        Die Daten liegen in Frankfurt.
      </div>
    </div>

    <div className="px-4 py-3 border-t border-violet-400/30 flex-shrink-0">
      <button
        type="button"
        onClick={onLogout}
        className="inline-flex w-full items-center gap-2 rounded-lg border border-violet-400/30 bg-[#07070a] px-3 py-1.5 text-left text-sm text-red-300 hover:bg-red-500/10"
      >
        <LogOut className="w-4 h-4" /> Abmelden
      </button>
    </div>
  </>
);
